import { useAtomValue } from "jotai";
import type { PageProps } from "next";
import { useEffect } from "react";
import { graphql, usePaginationFragment, usePreloadedQuery } from "react-relay";
import { withRelay } from "relay-nextjs";
import type { RelayComponent } from "relay-nextjs";

import { data } from "shared/data";
import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";
import { parseQueryString } from "shared/lib/utils";

import { DynamicAdSlot } from "scmp-app/components/advertisement/ad-slots/ad-slot/dynamics";
import { useTopBannerAdSlot } from "scmp-app/components/advertisement/ad-slots/top-banner-ad-slot/hooks";
import { TargetingPaidType } from "scmp-app/components/advertisement/ad-slots/types";
import { useAppBarStickyHeightValue } from "scmp-app/components/app-bar/hooks";
import { AppFooter } from "scmp-app/components/app-footer";
import { AuthorAvatarContextProvider } from "scmp-app/components/author/author-avatar/contexts";
import { AuthorContentList } from "scmp-app/components/author/author-content-list";
import { AuthorHeader } from "scmp-app/components/author/author-header";
import { AuthorLinkingData } from "scmp-app/components/author/author-linking-data";
import { AuthorRelatedTopics } from "scmp-app/components/author/author-related-topics";
import { AuthorSeo } from "scmp-app/components/author/author-seo";
import { isSCMPColumnist, isSCMPEditorial } from "scmp-app/components/author/helpers";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { trackingAtom } from "scmp-app/components/tracking";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import type { authorAuthorContentListAuthor$key } from "scmp-app/queries/__generated__/authorAuthorContentListAuthor.graphql";
import type { authorPageQuery } from "scmp-app/queries/__generated__/authorPageQuery.graphql";

import { Bio, Container, Left, LeftAdSlotContainer, List, Right } from "./styles";

const query = graphql`
  query authorPageQuery(
    $entityId: String!
    $applicationIds: [String]
    $after: String
    $count: Int = 30
  ) {
    author(filter: { entityId: $entityId }) {
      entityId
      entityUuid
      name
      urlAlias
      types
      ...authorContentListAuthor
        @arguments(applicationIds: $applicationIds, after: $after, count: $count)
      ...authorAuthorContentListAuthor
        @arguments(applicationIds: $applicationIds, after: $after, count: $count)
      ...authorRelatedTopicsAuthor
        @arguments(applicationIds: $applicationIds, after: $after, count: $count)
      ...authorLinkingDataAuthor
      ...authorHeaderAuthor
      ...authorSeoAuthor
    }
  }
`;

const AuthorPage: RelayComponent<unknown, authorPageQuery> = ({ preloadedQuery }) => {
  const data = usePreloadedQuery(query, preloadedQuery);
  const isDesktop = useResponsive(theme.breakpoints.up("desktop"), true);

  const { trackPageView } = useAtomValue(trackingAtom);

  useEffect(() => {
    const { entityId, entityUuid, name, urlAlias } = data.author;
    trackPageView?.({
      authors: name,
      contentType: "Author",
      entityId,
      entityUuid,
      page: "Author",
      path: urlAlias,
    });
  }, [trackPageView, data.author]);

  const { data: contentList, hasNext: hasNextPage } = usePaginationFragment<
    authorPageQuery,
    authorAuthorContentListAuthor$key
  >(
    graphql`
      fragment authorAuthorContentListAuthor on Author
      @argumentDefinitions(
        after: { type: "String" }
        count: { type: "Int", defaultValue: 30 }
        applicationIds: { type: "[String]", defaultValue: [] }
      )
      @refetchable(queryName: "authorAuthorContentListPaginationQuery") {
        contents(
          after: $after
          first: $count
          filter: { applicationIds: $applicationIds }
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
        ) @connection(key: "authorAuthorContentList__author_contents") {
          edges {
            __typename
          }
        }
      }
    `,
    data.author,
  );

  const totalNumberOfContents = contentList?.contents?.edges?.length ?? 0;

  const { appBarStickyHeight } = useAppBarStickyHeightValue();

  useTopBannerAdSlot({
    desktop: {
      adUnit: "d_banner2b",
      sizes: [
        [970, 250],
        [728, 90],
        [970, 90],
      ],
      targeting: {
        paid: TargetingPaidType.NotArticle,
        sctid: data.author.entityId,
      },
      zone: "comment_author/home",
    },
    mobile: {
      adUnit: "m_banner3",
      targeting: {
        paid: TargetingPaidType.NotArticle,
        sctid: data.author.entityId,
      },
      zone: "comment_author/home",
    },
  });

  return (
    <AuthorAvatarContextProvider
      showBorderColor={
        isSCMPEditorial([data.author.entityId]) || isSCMPColumnist(data.author.types)
      }
    >
      <AuthorSeo reference={data.author} />
      <AuthorLinkingData reference={data.author} />
      <Container>
        <Left $stickyStackedHeight={appBarStickyHeight}>
          <LeftAdSlotContainer>
            {isDesktop && totalNumberOfContents > 4 && (
              <DynamicAdSlot
                adUnit="d_sky1b"
                sizes={[
                  [120, 160],
                  [160, 600],
                ]}
                targeting={{ paid: TargetingPaidType.NotArticle, sctid: data.author.entityId }}
                zone="comment_author/home"
              />
            )}
          </LeftAdSlotContainer>
        </Left>
        <Bio>
          <AuthorHeader reference={data.author} />
        </Bio>
        <List>
          <AuthorContentList reference={data.author} />
        </List>
        <Right>
          <AuthorRelatedTopics reference={data.author} />
        </Right>
      </Container>
      {!hasNextPage && <AppFooter />}
    </AuthorAvatarContextProvider>
  );
};

AuthorPage.displayName = "AuthorPage";

const pageProps: PageProps = {
  appBarConfiguration: {
    adSlotVariants: {
      large: {
        height: 250,
        padding: 20,
      },
      small: {
        height: 100,
        padding: 10,
      },
    },
    hasDesktopAd: true,
    hasMobileAd: true,
    variant: "scmp/slim",
  },
};

export default withRelay(AuthorPage, query, {
  clientSideProps: () => pageProps,
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: () => Promise.resolve(pageProps),
  variablesFromContext(context) {
    return {
      applicationIds: [data.application.scmp.entityUuid],
      entityId: parseQueryString(context.query.entityId) ?? "",
    };
  },
});
